import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

let globalOtp = ''; // Global variable to store OTP

// Validation schema using Yup for Feedback form
const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    mobileNo: Yup.string()
        .matches(/^\d{10}$/, 'Mobile number must be 10 digits')
        .required('Mobile number is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    message: Yup.string().required('Message is required'),
    relatedTo: Yup.string().required('Please specify what your feedback is related to'),
    callMailAccepted: Yup.bool().oneOf([true], 'You must accept the terms'),
});

const errorMessageStyle = {
    color: 'red',
    fontSize: '0.9em',
    marginTop: '4px',
};
// Feedback Form Component
const FeedbackForm = () => {
    const [otpSent, setOtpSent] = useState(false);
    const [otpInput, setOtpInput] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [thankYouMessage, setThankYouMessage] = useState(false); // State for Thank You message
    const [loading, setLoading] = useState(false); // Loader state for OTP and form submission
    const [timer, setTimer] = useState(60); // Timer for OTP expiration
    const [timerActive, setTimerActive] = useState(false); // To control the timer
    const [otpResendEnabled, setOtpResendEnabled] = useState(false);
    const [mobileDisabled, setMobileDisabled] = useState(false); 

    // Handle sending OTP
    const handleSendOtp = async (mobileNo) => {
        try {
            setLoading(true); // Start loading indicator
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/UserDetails/CheckValidNumberOrNot?mobileNo=${mobileNo}`);
            if (response.data.success) {
                setOtpSent(true);
                setOtpError('');
                setOtpInput(''); // Clear any previous OTP input
                setOtpVerified(false); // Reset verification status
                setTimer(60); // Reset the timer
                setTimerActive(true); // Start the timer
                setOtpResendEnabled(false); // Disable resend button
                setMobileDisabled(true); 
            } else {
                setOtpError('Failed to send OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            setOtpError('An error occurred while sending OTP. Please try again.');
        } finally {
            setLoading(false); // Stop loading indicator
        }
    };

    // Handle OTP verification
    const handleOtpVerify = async (mobileNo) => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/UserDetails/VerifyOtp?mobileNo=${mobileNo}&otp=${otpInput}`);
            if (response.data.success) {
                setOtpVerified(true);
                setOtpError('');
                toast.success('OTP verified successfully!');
                setTimerActive(false); // Stop the timer
            } else {
                setOtpError(response.data.message || 'Invalid OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setOtpError('An error occurred while verifying OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Set timeout for OTP expiration
    useEffect(() => {
        if (timerActive && timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(interval);
        } else if (timer === 0) {
            setOtpSent(false); // Reset OTP sent state
            setOtpError('OTP expired. Please request a new OTP.');
            setOtpVerified(false); // Reset verified status
            setTimerActive(false); // Stop the timer
            setOtpResendEnabled(true); // Enable resend button
            setMobileDisabled(false); 
        }
    }, [timerActive, timer]);
    // Handle form submission
    const handleSubmit = async (values, { resetForm }) => {
        if (!otpVerified) {
            toast.error('Please verify the OTP before submitting the form.');
            return;
        }

        try {
            setLoading(true); // Start loading indicator
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Enquiry/InsertFeedback`, {
                name: values.name,
                mobileNo: values.mobileNo,
                email: values.email,
                message: values.message,
                relatedTo: values.relatedTo,
            });
            if (response.data.success) {
                setThankYouMessage(true); // Show thank-you message
                setOtpSent(false);
                setOtpVerified(false);
                setOtpInput('');
                resetForm();

                // Auto-refresh the form after a few seconds
                setTimeout(() => {
                    setThankYouMessage(false); // Hide the thank-you message
                    window.location.reload(); // Refresh the page
                }, 6000); // Refresh after 3 seconds
            } else {
                toast.error('Failed to submit feedback. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('An error occurred while submitting feedback. Please try again.');
        } finally {
            setLoading(false); // Stop loading indicator
        }
    };

    return (
        <Formik
            initialValues={{
                name: '',
                mobileNo: '',
                email: '',
                message: '',
                relatedTo: '', 
                callMailAccepted: false,
            }}
            validationSchema={validationSchema}
            validateOnBlur={true} // Enable validation on blur
            validateOnChange={true} // Enable validation on change
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur }) => (
                <Form>
                    <div className="uk-child-width-1-2@m uk-grid-small uk-grid-match" data-uk-grid>
                        <div className='uk-width-1-1'>
                            <div className="uk-text-left">
                                <label className="uk-form-label uk-text-bold">Name:</label>
                                <Field type="text" name="name" className='uk-input' />
                                <ErrorMessage style={errorMessageStyle} name="name" component="div" className="error-message" />
                            </div>
                        </div>
                        <div>
                            <div className="uk-text-left">
                                <label className="uk-form-label uk-text-bold">Mobile No:</label>
                                <Field
                                    type="text"
                                    name="mobileNo"
                                    className='uk-input'
                                    value={values.mobileNo} // Bind value from Formik
                                    disabled={mobileDisabled} // Disable input if OTP is verified// Disable mobile number field after OTP verification
                                />
                                <ErrorMessage style={errorMessageStyle} name="mobileNo" component="div" className="error-message" />
                                {!otpVerified && (
                                    <button
                                        type="button" className='uk-margin-small-top'
                                        onClick={() => handleSendOtp(values.mobileNo)}
                                        disabled={!values.mobileNo || otpSent || loading} // Disable if mobile number is empty or loading
                                    >
                                        {loading ? 'Sending...' : 'Send OTP'}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div>
                            <div>
                                <div className="uk-text-left">
                                    {otpSent && (
                                        <div>
                                            <label className="uk-form-label uk-text-bold">Enter OTP:</label>
                                            <Field
                                                type="text"
                                                name="otpsend"
                                                className="uk-input"
                                                value={otpInput}
                                                onChange={(e) => setOtpInput(e.target.value)}
                                                disabled={otpVerified} // Disable input if OTP is verified
                                                required
                                            />
                                            {!otpVerified && (
                                                <button
                                                    type="button"
                                                    className="uk-margin-small"
                                                    onClick={() => handleOtpVerify(values.mobileNo)}
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Verifying...' : 'Verify OTP'}
                                                </button>
                                            )}
                                            {otpError && <span style={{ color: 'red' }}>{otpError}</span>}
                                            <div>
                                                {otpVerified ? (
                                                    <span style={{ color: 'green' }}>OTP is verified</span>
                                                ) : (
                                                    <span style={{ color: 'red' }}>{timer > 0 ? `${timer} seconds remaining` : 'OTP expired'}</span>
                                                )}
                                            </div>
                                            {otpResendEnabled && !otpVerified && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSendOtp(values.mobileNo)}
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Sending...' : 'Resend OTP'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="uk-text-left">
                                <label className="uk-form-label uk-text-bold">Email:</label>
                                <Field type="email" className='uk-input' name="email" />
                                <ErrorMessage style={errorMessageStyle} name="email" component="div" className="error-message" />
                            </div>
                        </div>
                        <div>
                            <div className="uk-text-left">
                                <label className="uk-form-label uk-text-bold">Related To:</label>
                                <Field type="text" name="relatedTo" className='uk-input'/>
                                <ErrorMessage style={errorMessageStyle} name="relatedTo" component="div" className="error-message" />
                            </div>
                        </div>
                        <div className='uk-width-1-1'>
                            <div className="uk-text-left">
                                <label className="uk-form-label uk-text-bold">Message:</label>
                                <Field as="textarea" name="message" rows="3" className='uk-textarea' />
                                <ErrorMessage name="message" component="div" className="error-message" />
                            </div>
                        </div>
                        <div className='uk-width-1-1'>
                            <div className="uk-text-left">
                                <Field type="checkbox" name="callMailAccepted" className="uk-checkbox" />
                                <label className="uk-form-label uk-text-bold uk-margin-small-left">
                                I agree and allow STRATOS AIRMAIL representative to call or e-mail regarding the enquiry.
                                </label>
                                <ErrorMessage style={errorMessageStyle} name="callMailAccepted" component="div" className="error-message" />
                            </div>
                        </div>
                    </div>
                    <div>
                        <button
                            type="submit"
                            className="button uk-button-primary uk-margin-top"
                            disabled={!otpVerified || loading}
                        >
                           {loading ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                    <ToastContainer 
                                position="top-left" // Set the position (e.g., top-right, bottom-left)
                                autoClose={5000} // Auto close after 5 seconds
                                hideProgressBar={false} // Show progress bar
                                newestOnTop={false} // Newest toasts appear on top
                                closeOnClick // Close toast on click
                                rtl={false} // Right to left layout
                                pauseOnFocusLoss // Pause when the window is focused
                                draggable // Allow dragging
                                pauseOnHover // Pause when hovered
                                style={{ width: '300px' }}
                            />
                    {thankYouMessage && (
                        <div className="uk-alert-success" uk-alert="true">
                            Thank you! Your feedback has been submitted successfully.
                        </div>
                    )}
                </Form>
            )}
        </Formik>
    );
};

export default FeedbackForm;
